import { ArrowLeft, Download, History } from "lucide-react";
import { useMemo, useState } from "react";

import {
  formatRate,
  RATE_TABLE_COLUMNS,
  SEASON_TABLE_COLUMNS,
  type CurrentStatRow,
} from "../../domain/baseballStats";
import { Button } from "@/shared/ui/button";
import { PageHeader } from "@/shared/ui/page-header";
import {
  nextSortState,
  SortableTableHead,
  sortRows,
  type SortState,
} from "@/shared/ui/sortable-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/ui/table";
import { actionKeys } from "../../hooks/actionKeys";
import type { IntentStatusView } from "../../hooks/useIntentStatus";
import { EMPTY_INTENT_STATUS } from "../../hooks/useIntentStatus";

interface BoxScoreScreenProps {
  rows: CurrentStatRow[];
  archivedGameCount: number;
  serverBusy: boolean;
  gameId?: string | null;
  actionStatus?: IntentStatusView;
  exportError?: string | null;
  onShowSeason: () => void;
  onShowGame: () => void;
  onExport: () => Promise<void>;
  onArchive: () => Promise<boolean>;
}

type CurrentSortKey =
  | "PA"
  | (typeof SEASON_TABLE_COLUMNS)[number]
  | (typeof RATE_TABLE_COLUMNS)[number];

const DEFAULT_SORT: SortState<CurrentSortKey> = {
  key: "OPS",
  direction: "desc",
};

const STAT_TITLES = {
  PA: "Plate appearances",
  AB: "At bats",
  H: "Hits",
  "1B": "Singles",
  "2B": "Doubles",
  "3B": "Triples",
  HR: "Home runs",
  BB: "Walks",
  K: "Strikeouts",
  RBI: "Runs batted in",
  R: "Runs",
  AVG: "Batting average",
  OBP: "On-base percentage",
  SLG: "Slugging percentage",
  OPS: "On-base plus slugging",
  ISO: "Isolated power",
} as const;

type StatAbbreviation = keyof typeof STAT_TITLES;

export function BoxScoreScreen({
  rows,
  archivedGameCount,
  serverBusy,
  gameId = null,
  actionStatus = EMPTY_INTENT_STATUS,
  exportError = null,
  onShowSeason,
  onShowGame,
  onExport,
  onArchive,
}: BoxScoreScreenProps) {
  const [sortState, setSortState] = useState(DEFAULT_SORT);
  const sortedRows = useMemo(
    () => sortRows(rows, sortState, currentSortValue),
    [rows, sortState],
  );
  const onSort = (key: CurrentSortKey) =>
    setSortState((current) => nextSortState(current, key));
  const exportPending = actionStatus.isPending(actionKeys.exportStats);
  const archivePending = gameId
    ? actionStatus.isPending(actionKeys.archive(gameId)) ||
      actionStatus.isPending(actionKeys.finalize(gameId))
    : serverBusy;

  return (
    <div className="mx-auto w-full max-w-5xl space-y-3">
      <PageHeader
        title="BOX SCORE"
        eyebrow="Current game"
        description={`${archivedGameCount} game${
          archivedGameCount === 1 ? "" : "s"
        } saved in history`}
        actions={
          <>
            <Button
              type="button"
              size="xs"
              variant="outline"
              onClick={onShowSeason}
            >
              SEASON
            </Button>
            <Button
              type="button"
              size="xs"
              variant="outline"
              aria-label="← GAME"
              onClick={onShowGame}
            >
              <ArrowLeft aria-hidden="true" />
              GAME
            </Button>
          </>
        }
      />

      <Table aria-label="Current game batting statistics">
        <TableHeader>
          <TableRow className="border-border hover:bg-surface-raised">
            <TableHead
              scope="col"
              className="sticky left-0 z-10 min-w-32 bg-surface-raised text-left"
            >
              PLAYER
            </TableHead>
            <SortableTableHead
              sortKey="PA"
              sortState={sortState}
              onSort={onSort}
            >
              <StatHeading value="PA" />
            </SortableTableHead>
            {SEASON_TABLE_COLUMNS.map((heading) => (
              <SortableTableHead
                key={heading}
                sortKey={heading}
                sortState={sortState}
                onSort={onSort}
              >
                <StatHeading value={heading} />
              </SortableTableHead>
            ))}
            {RATE_TABLE_COLUMNS.map((heading) => (
              <SortableTableHead
                key={heading}
                sortKey={heading}
                sortState={sortState}
                onSort={onSort}
                className={heading === "OPS" ? "bg-warning/10" : undefined}
              >
                <StatHeading value={heading} />
              </SortableTableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedRows.map(({ p, s, pa, avg, obp, slg, ops, iso }) => (
            <TableRow key={p.id}>
              <TableCell
                title={p.name}
                className="sticky left-0 z-10 max-w-40 overflow-hidden bg-surface text-left font-sans text-sm font-bold text-ellipsis"
              >
                {p.name}
                {p.num != null ? (
                  <span className="font-mono text-[0.68rem] font-normal text-foreground-muted">
                    {" "}
                    #{p.num}
                  </span>
                ) : null}
              </TableCell>
              <TableCell>{pa}</TableCell>
              <TableCell>{s.AB}</TableCell>
              <TableCell>{s.H}</TableCell>
              <TableCell>{s["1B"]}</TableCell>
              <TableCell>{s["2B"]}</TableCell>
              <TableCell>{s["3B"]}</TableCell>
              <TableCell>{s.HR}</TableCell>
              <TableCell>{s.BB}</TableCell>
              <TableCell>{s.K}</TableCell>
              <TableCell>{s.RBI}</TableCell>
              <TableCell>{s.R}</TableCell>
              <TableCell>{formatRate(avg)}</TableCell>
              <TableCell>{formatRate(slg)}</TableCell>
              <TableCell className="bg-warning/10 font-bold text-warning">
                {formatRate(ops)}
              </TableCell>
              <TableCell>{formatRate(obp)}</TableCell>
              <TableCell>{formatRate(iso)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <div className="flex flex-col items-start gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          aria-label="⬇ EXPORT TO EXCEL (.xlsx)"
          onClick={onExport}
          pending={exportPending}
          pendingLabel="EXPORTING…"
        >
          <Download aria-hidden="true" />
          EXPORT TO EXCEL (.xlsx)
        </Button>
        {exportError ? (
          <p role="alert" className="m-0 text-sm text-danger">
            {exportError}
          </p>
        ) : null}
        <Button
          type="button"
          size="default"
          pending={archivePending}
          pendingLabel="SAVING…"
          onClick={onArchive}
          className="w-full whitespace-normal"
        >
          <History aria-hidden="true" />
          END GAME — SAVE TO HISTORY & RESET
        </Button>
      </div>
    </div>
  );
}

function StatHeading({ value }: { value: StatAbbreviation }) {
  return (
    <abbr
      title={STAT_TITLES[value]}
      className="cursor-help decoration-dotted underline-offset-2"
    >
      {value}
    </abbr>
  );
}

function currentSortValue(
  row: CurrentStatRow,
  key: CurrentSortKey,
): number | undefined {
  switch (key) {
    case "PA":
      return row.pa;
    case "AVG":
      return row.avg;
    case "OBP":
      return row.obp;
    case "SLG":
      return row.slg;
    case "OPS":
      return row.ops;
    case "ISO":
      return row.iso;
    default:
      return row.s[key as keyof CurrentStatRow["s"]];
  }
}
